import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, useTheme } from "@mui/material";
import ReportCard from "components/ReportCard";
import ReportDowntimeBarChart from "components/ReportDowntimeBarChart";
import { computeOeeRatios, OeeMetricEnum, sumOeeParts, topMachineCenterLosses } from "utilities/oeeSummary";
import { useStyles } from "./OeeLossByMachineCenter.styles";
import { IOeeLossByMachineCenterProps, IOeeLossChartRow, OeeLossFactor } from "./OeeLossByMachineCenterInterfaces";

const TITLE_KEYS: Record<
    OeeLossFactor,
    { noLoss: string; notMeasured: string; title: string; titleWithProductionLine: string }
> = {
    [OeeMetricEnum.PERFORMANCE]: {
        noLoss: "locations.oee_summary_dashboard.performance_loss.no_loss",
        notMeasured: "locations.oee_summary_dashboard.performance_loss.not_measured",
        title: "locations.oee_summary_dashboard.performance_loss.title",
        titleWithProductionLine: "locations.oee_summary_dashboard.performance_loss.title_with_production_line",
    },
    [OeeMetricEnum.QUALITY]: {
        noLoss: "locations.oee_summary_dashboard.quality_loss.no_loss",
        notMeasured: "locations.oee_summary_dashboard.quality_loss.not_measured",
        title: "locations.oee_summary_dashboard.quality_loss.title",
        titleWithProductionLine: "locations.oee_summary_dashboard.quality_loss.title_with_production_line",
    },
};

const OeeLossByMachineCenter = ({ factor, loading, productionLineName, rows }: IOeeLossByMachineCenterProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const lossColor = useTheme().palette.error.main;
    const titleKeys = TITLE_KEYS[factor];

    /** Nivo draws the first horizontal bar at the bottom, so ascending order puts the largest loss on top. */
    const chartData = useMemo<IOeeLossChartRow[]>(
        () =>
            topMachineCenterLosses(rows, factor)
                .map(({ lossPercent, machineCenterId, machineCenterName }) => ({
                    loss: lossPercent,
                    lossColor,
                    machineCenter: machineCenterName,
                    machineCenterId,
                    totalDowntimeByMill: lossPercent,
                }))
                .reverse(),
        [factor, lossColor, rows],
    );

    /**
     * An empty chart is not always missing data: with rows in hand the factor was either
     * measured and nothing was lost (good news) or never measured for this selection
     * (unknown). Measured-ness is the same rule topMachineCenterLosses applies per machine
     * center, rolled up over the selection. Only a request that returned no rows at all
     * falls through to the generic no-data message.
     */
    const emptyStateTitle = useMemo(() => {
        if (rows.length === 0 || chartData.length > 0) {
            return undefined;
        }

        const parts = sumOeeParts(rows);
        const measured =
            factor === OeeMetricEnum.PERFORMANCE ? computeOeeRatios(parts).performance !== null : parts.totalCount > 0;

        return t(measured ? titleKeys.noLoss : titleKeys.notMeasured);
    }, [chartData.length, factor, rows, t, titleKeys]);

    const title = productionLineName
        ? t(titleKeys.titleWithProductionLine, { productionLine: productionLineName })
        : t(titleKeys.title);

    return (
        <ReportCard title={title} showNoData={!loading && chartData.length === 0} noDataTitle={emptyStateTitle}>
            {loading ? (
                <div className={classes.loadingWrapper}>
                    <CircularProgress size={24} color="info" />
                </div>
            ) : (
                <div className={classes.chartWrapper}>
                    <ReportDowntimeBarChart
                        data={chartData}
                        index="machineCenter"
                        showPercentLayerOnTotalBar
                        noLegend
                        noTrend
                    />
                </div>
            )}
        </ReportCard>
    );
};

export default OeeLossByMachineCenter;
